import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ArrowUpDown, ChevronDown, ChevronUp, Columns3, ExternalLink, Plus } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { TicketCard } from '@/components/cards/TicketCard';
import { InlineEdit } from '@/components/InlineEdit';
import { AssignedResourcePicker } from '@/components/AssignedResourcePicker';
import { BulkEditTicketsDialog } from '@/components/BulkEditTicketsDialog';
import { FilterBar } from '@/components/FilterBar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCompanyLookup, usePicklists, useResources, useTicketDescriptions, useTicketHoverPrefetch, useTickets, type TicketFilters } from '@/api/hooks';
import type { Picklists, PicklistValue, Ticket } from '@/api/client';
import { cn } from '@/lib/utils';
import { ROUTES, ticketPath } from '@/lib/routes';
import { TicketQueueAssistant } from '@/components/TicketAssistant';
import { TicketDescriptionCell, useDescriptionTooltip } from '@/components/TicketDescriptionCell';
import { MD_UP, useMediaQuery } from '@/lib/useMediaQuery';

export interface TicketsViewConfig {
  title: string;
  storageKeyFilters: string;
  storageKeySort: string;
  defaultFilters: TicketFilters;
  queueTabs?: { label: string; id: number | null }[];
  /** Show the Assignee column by default (e.g. on Dispatch). Users can still toggle it via the column picker. */
  showAssignee?: boolean;
  /**
   * This view only ever shows unassigned tickets (defaultFilters.unassigned === true).
   * Hides the "My tickets / All tickets" toggle (which would contradict `unassigned`) and
   * surfaces an "Unassigned" subtitle.
   */
  unassignedOnly?: boolean;
}

type ColumnKey =
  | 'id' | 'title' | 'description' | 'company' | 'status' | 'priority' | 'queueID'
  | 'ticketType' | 'issueType' | 'subIssueType'
  | 'assignee' | 'dueDateTime' | 'lastActivityDate'
  | 'openInAutotask';

const AUTOTASK_TICKET_URL = 'https://ww31.autotask.net/Mvc/ServiceDesk/TicketDetail.mvc?ticketId=';

type SortKey = Exclude<ColumnKey, 'openInAutotask' | 'description'>;
type SortDir = 'asc' | 'desc';
type SortState = { key: SortKey; dir: SortDir } | null;

interface ColumnDef {
  key: ColumnKey;
  label: string;
  defaultWidth: number;
  minWidth: number;
  /** If true, can't be hidden via the picker (e.g. #, Title). */
  required?: boolean;
  /** If true, this column has no sort affordance (header is plain text). */
  unsortable?: boolean;
}

const ALL_COLUMNS: ColumnDef[] = [
  { key: 'id',               label: '#',        defaultWidth: 130, minWidth: 70,  required: true },
  { key: 'title',            label: 'Title',    defaultWidth: 320, minWidth: 140, required: true },
  { key: 'description',      label: 'Description', defaultWidth: 260, minWidth: 100, unsortable: true },
  { key: 'company',          label: 'Company',  defaultWidth: 200, minWidth: 80  },
  { key: 'status',           label: 'Status',   defaultWidth: 175, minWidth: 90  },
  { key: 'priority',         label: 'Priority', defaultWidth: 120, minWidth: 80  },
  { key: 'queueID',          label: 'Queue',    defaultWidth: 160, minWidth: 90  },
  { key: 'ticketType',       label: 'Ticket Type', defaultWidth: 140, minWidth: 80 },
  { key: 'issueType',        label: 'Type',     defaultWidth: 140, minWidth: 80  },
  { key: 'subIssueType',     label: 'Sub-Type', defaultWidth: 160, minWidth: 80  },
  { key: 'assignee',         label: 'Assignee', defaultWidth: 170, minWidth: 100 },
  { key: 'dueDateTime',      label: 'Due',      defaultWidth: 110, minWidth: 70  },
  { key: 'lastActivityDate', label: 'Activity', defaultWidth: 130, minWidth: 80  },
  { key: 'openInAutotask',   label: '',         defaultWidth: 40,  minWidth: 40, required: true, unsortable: true },
];

const COL_BY_KEY = new Map(ALL_COLUMNS.map((c) => [c.key, c]));

const NO_TICKETS: Ticket[] = [];
const NO_OPTIONS: PicklistValue[] = [];
/** Typing in the search box: write the filters to localStorage once typing pauses, not per keystroke. */
const FILTER_SAVE_DELAY = 400;

function loadFilters(key: string, defaults: TicketFilters): TicketFilters {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...defaults, ...JSON.parse(raw) } : defaults;
  } catch {
    return defaults;
  }
}

function loadSort(key: string): SortState {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as SortState) : null;
  } catch {
    return null;
  }
}

function loadWidths(key: string): Partial<Record<ColumnKey, number>> {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Columns added after people saved their own column choice. Each is switched on once for everyone (their saved
 * choice would otherwise hide it for good); after that, hiding it sticks. `::seen` records what's been offered.
 */
const NEW_COLUMNS: ColumnKey[] = ['description'];

function loadVisible(key: string, fallback: ColumnKey[]): Set<ColumnKey> {
  try {
    const raw = localStorage.getItem(key);
    const seenKey = `${key}::seen`;
    const seen = new Set(JSON.parse(localStorage.getItem(seenKey) ?? '[]') as ColumnKey[]);
    localStorage.setItem(seenKey, JSON.stringify(NEW_COLUMNS));
    if (raw) {
      const set = new Set(JSON.parse(raw) as ColumnKey[]);
      const fresh = NEW_COLUMNS.filter((k) => !seen.has(k));
      if (fresh.length) {
        for (const k of fresh) set.add(k);
        localStorage.setItem(key, JSON.stringify([...set]));
      }
      return set;
    }
  } catch {}
  return new Set(fallback);
}

function defaultVisibleColumns(showAssignee: boolean): ColumnKey[] {
  // `openInAutotask` is required (always visible) so it's not in this list — required
  // columns are added automatically when building visibleCols below.
  const keys: ColumnKey[] = [
    'id', 'title', 'description', 'company', 'status', 'priority', 'queueID',
    'issueType', 'subIssueType',
    ...(showAssignee ? (['assignee'] as ColumnKey[]) : []),
    'dueDateTime', 'lastActivityDate',
  ];
  return keys;
}

export function TicketsView({ config }: { config: TicketsViewConfig }) {
  const navigate = useNavigate();
  const widthsKey = `${config.storageKeyFilters}::widths`;
  const visibleKey = `${config.storageKeyFilters}::visible`;

  const [filters, setFilters] = useState<TicketFilters>(() => loadFilters(config.storageKeyFilters, config.defaultFilters));
  const [sort, setSort] = useState<SortState>(() => loadSort(config.storageKeySort));
  const [widths, setWidths] = useState<Partial<Record<ColumnKey, number>>>(() => loadWidths(widthsKey));
  const [visible, setVisible] = useState<Set<ColumnKey>>(() => loadVisible(visibleKey, defaultVisibleColumns(!!config.showAssignee)));

  // ----- Bulk selection (desktop table) -----
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  // Text search is applied client-side over the loaded rows (see filteredItems below),
  // so it is not sent to the server — this avoids a request on every keystroke and lets
  // us match on company name (already resolved client-side) as well as title / #.
  const tickets = useTickets({ ...filters, search: '' }, { scoped: true });
  const picklists = usePicklists();
  const resources = useResources();
  // Prefetch a ticket only when the pointer rests on its link (each prefetch = 4 Autotask calls).
  const hoverPrefetch = useTicketHoverPrefetch();
  // Phones get the card list, desktops the table — only one is rendered.
  const isDesktop = useMediaQuery(MD_UP);

  // ----- Persistence -----
  // Debounced: the search box lives in `filters`, so this would otherwise write on every keystroke.
  const latestFilters = useRef({ key: config.storageKeyFilters, filters });
  latestFilters.current = { key: config.storageKeyFilters, filters };
  useEffect(() => {
    const t = window.setTimeout(() => localStorage.setItem(config.storageKeyFilters, JSON.stringify(filters)), FILTER_SAVE_DELAY);
    return () => window.clearTimeout(t);
  }, [filters, config.storageKeyFilters]);
  // …and flushed on leaving the page so the last keystrokes aren't lost.
  useEffect(() => () => {
    const { key, filters: f } = latestFilters.current;
    localStorage.setItem(key, JSON.stringify(f));
  }, []);
  useEffect(() => {
    if (sort) localStorage.setItem(config.storageKeySort, JSON.stringify(sort));
    else localStorage.removeItem(config.storageKeySort);
  }, [sort, config.storageKeySort]);
  useEffect(() => { localStorage.setItem(widthsKey, JSON.stringify(widths)); }, [widths, widthsKey]);
  useEffect(() => { localStorage.setItem(visibleKey, JSON.stringify(Array.from(visible))); }, [visible, visibleKey]);

  // ----- Derived data -----
  const items = tickets.data?.items ?? NO_TICKETS;

  const companyIds = useMemo(() => items.map((t) => t.companyID).filter((v): v is number => v != null), [items]);
  const companies = useCompanyLookup(companyIds);
  const companyMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of companies.data?.items ?? []) m.set(c.id, c.companyName);
    return m;
  }, [companies.data]);

  const resourceMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of resources.data?.items ?? []) m.set(r.id, `${r.firstName} ${r.lastName}`.trim());
    return m;
  }, [resources.data]);

  const pl: Picklists | undefined = picklists.data;

  // Client-side text search over the loaded rows: title, ticket # and company name. Deferred, so typing
  // stays responsive while the grid re-filters at a lower priority.
  const deferredSearch = useDeferredValue(filters.search);
  const searchQuery = deferredSearch.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    return items.filter((t) => {
      const company = t.companyID != null ? (companyMap.get(t.companyID) ?? '') : '';
      return (
        (t.title ?? '').toLowerCase().includes(searchQuery) ||
        (t.ticketNumber ?? '').toLowerCase().includes(searchQuery) ||
        String(t.id).includes(searchQuery) ||
        company.toLowerCase().includes(searchQuery)
      );
    });
  }, [items, searchQuery, companyMap]);

  const sortedItems = useMemo(() => {
    if (!sort) return filteredItems;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const labelMaps: Record<string, Map<number, string>> = {
      status: pickToMap(pl?.status),
      priority: pickToMap(pl?.priority),
      queueID: pickToMap(pl?.queueID),
      ticketType: pickToMap(pl?.ticketType),
      issueType: pickToMap(pl?.issueType),
      subIssueType: pickToMap(pl?.subIssueType),
    };
    const get = (t: Ticket): string | number => {
      switch (sort.key) {
        case 'id': return t.id;
        case 'title': return (t.title ?? '').toLowerCase();
        case 'company': return (t.companyID != null ? companyMap.get(t.companyID) ?? '' : '').toLowerCase();
        case 'status': {
          const label = (labelMaps.status.get(t.status) ?? '').toLowerCase();
          return STATUS_WORKFLOW_ORDER[label] ?? 999;
        }
        case 'priority': return (labelMaps.priority.get(t.priority) ?? '').toLowerCase();
        case 'queueID': return (t.queueID != null ? labelMaps.queueID.get(t.queueID) ?? '' : '').toLowerCase();
        case 'ticketType': return (t.ticketType != null ? labelMaps.ticketType.get(t.ticketType) ?? '' : '').toLowerCase();
        case 'issueType': return (t.issueType != null ? labelMaps.issueType.get(t.issueType) ?? '' : '').toLowerCase();
        case 'subIssueType': return (t.subIssueType != null ? labelMaps.subIssueType.get(t.subIssueType) ?? '' : '').toLowerCase();
        case 'assignee': return t.assignedResourceID != null ? (resourceMap.get(t.assignedResourceID) ?? '').toLowerCase() : (dir === 1 ? '￿' : '');
        case 'dueDateTime': return t.dueDateTime ? new Date(t.dueDateTime).getTime() : (dir === 1 ? Number.MAX_SAFE_INTEGER : -1);
        case 'lastActivityDate': return t.lastActivityDate ? new Date(t.lastActivityDate).getTime() : 0;
      }
    };
    return [...filteredItems].sort((a, b) => {
      const va = get(a); const vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [filteredItems, sort, pl, companyMap, resourceMap]);

  // ----- Visible columns (preserve ALL_COLUMNS order) -----
  const visibleCols = useMemo(
    () => ALL_COLUMNS.filter((c) => c.required || visible.has(c.key)),
    [visible],
  );

  // ----- Descriptions -----
  // Not in the polled list: loaded once per list (by the loaded ids, so searching doesn't refetch), and only
  // when the table shows the Description column. One tooltip for the whole table.
  const showDescriptions = isDesktop && visibleCols.some((c) => c.key === 'description');
  const descIds = useMemo(() => (showDescriptions ? items.map((t) => t.id) : []), [items, showDescriptions]);
  const descriptions = useTicketDescriptions(descIds);
  const descTooltip = useDescriptionTooltip(descriptions);

  // ----- Selection bookkeeping -----
  // Drop any selected ids that have left the loaded list (filtered out, refetched, reassigned away).
  useEffect(() => {
    const present = new Set(items.map((t) => t.id));
    setSelected((prev) => {
      let changed = false;
      const next = new Set<number>();
      for (const id of prev) { if (present.has(id)) next.add(id); else changed = true; }
      return changed ? next : prev;
    });
  }, [items]);

  const allSelected = sortedItems.length > 0 && sortedItems.every((t) => selected.has(t.id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleRow = useCallback((id: number, on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }, []);

  function toggleSelectAll(on: boolean) {
    setSelected(on ? new Set(sortedItems.map((t) => t.id)) : new Set());
  }

  // ----- Column resize -----
  // While dragging, the <col>'s width is set directly (no React render per pointermove); the width is
  // committed to state (and so localStorage) once, on pointerup.
  const colRefs = useRef(new Map<ColumnKey, HTMLTableColElement>());
  const widthsRef = useRef(widths);
  widthsRef.current = widths;
  const dragRef = useRef<{ key: ColumnKey; startX: number; startWidth: number; width: number } | null>(null);
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const def = COL_BY_KEY.get(drag.key);
      const min = def?.minWidth ?? 60;
      drag.width = Math.max(min, drag.startWidth + (e.clientX - drag.startX));
      const col = colRefs.current.get(drag.key);
      if (col) col.style.width = `${drag.width}px`;
    };
    const onUp = () => {
      const drag = dragRef.current;
      if (!drag) return;
      document.body.style.cursor = '';
      dragRef.current = null;
      if (drag.width !== drag.startWidth) setWidths((w) => ({ ...w, [drag.key]: drag.width }));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  const startResize = useCallback((key: ColumnKey, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const def = COL_BY_KEY.get(key);
    const startWidth = widthsRef.current[key] ?? def?.defaultWidth ?? 120;
    dragRef.current = { key, startX: e.clientX, startWidth, width: startWidth };
    document.body.style.cursor = 'col-resize';
  }, []);

  function cycleSort(key: SortKey) {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  }

  function resetWidths() {
    setWidths({});
  }

  function toggleColumn(key: ColumnKey, on: boolean) {
    setVisible((prev) => {
      const next = new Set(prev);
      if (on) next.add(key); else next.delete(key);
      return next;
    });
  }

  function resetColumns() {
    setVisible(new Set(defaultVisibleColumns(!!config.showAssignee)));
  }

  // ----- Render -----
  const statusOpts = pl?.status ?? NO_OPTIONS;
  const priorityOpts = pl?.priority ?? NO_OPTIONS;
  const queueOpts = pl?.queueID ?? NO_OPTIONS;
  const statusLabelMap = useMemo(() => pickToMap(pl?.status), [pl?.status]);
  const priorityLabelMap = useMemo(() => pickToMap(pl?.priority), [pl?.priority]);
  const queueLabelMap = useMemo(() => pickToMap(pl?.queueID), [pl?.queueID]);
  const issueTypeOpts = pl?.issueType ?? NO_OPTIONS;
  const subIssueTypeOpts = pl?.subIssueType ?? NO_OPTIONS;
  const ticketTypeMap = useMemo(() => pickToMap(pl?.ticketType), [pl?.ticketType]);
  const issueTypeMap = useMemo(() => pickToMap(pl?.issueType), [pl?.issueType]);
  const subIssueTypeMap = useMemo(() => pickToMap(pl?.subIssueType), [pl?.subIssueType]);

  // One object for every row, rebuilt only when what it holds changes — rows are memoised on it.
  const cellCtx = useMemo<CellCtx>(() => ({
    companyMap,
    statusOpts, priorityOpts, queueOpts,
    issueTypeOpts, subIssueTypeOpts,
    ticketTypeMap, issueTypeMap, subIssueTypeMap,
    hoverPrefetch,
  }), [companyMap, statusOpts, priorityOpts, queueOpts, issueTypeOpts, subIssueTypeOpts,
    ticketTypeMap, issueTypeMap, subIssueTypeMap, hoverPrefetch]);

  return (
    <div className="mx-auto w-full max-w-[1900px] px-4 py-4">
      <PageHeader
        title={config.title}
        count={tickets.data ? items.length : null}
        isFetching={tickets.isFetching}
        subtitle={subtitleFor(filters, config)}
        actions={
          <div className="flex flex-col items-end gap-1 md:flex-row md:items-center md:gap-2">
            <ColumnPicker visible={visible} onToggle={toggleColumn} onReset={resetColumns} onResetWidths={resetWidths} />
            <Button size="sm" onClick={() => navigate(ROUTES.psa.tickets.new)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />New Ticket
            </Button>
          </div>
        }
      />

      <div className="sticky top-0 z-10 -mx-4 mt-2 border-b bg-background/80 px-4 backdrop-blur">
        {config.queueTabs && config.queueTabs.length > 0 ? (
          <QueueTabs tabs={config.queueTabs} filters={filters} onChange={setFilters} />
        ) : null}
        <div className="py-3">
          <FilterBar
            filters={filters}
            defaults={config.defaultFilters}
            onChange={setFilters}
            picklists={pl ? { status: statusOpts, priority: priorityOpts, queueID: queueOpts } : undefined}
            showAssignedToToggle={!config.unassignedOnly}
          />
        </div>
      </div>

      {tickets.error ? (
        <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          Failed to load tickets: {tickets.error instanceof Error ? tickets.error.message : 'Unknown error'}
        </div>
      ) : null}

      {/* Mobile card list */}
      {!isDesktop && (
      <div className="mt-4 space-y-2">
        {sortedItems.length === 0 && !tickets.isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No tickets match these filters.</div>
        ) : null}
        {sortedItems.map((t) => (
          <TicketCard
            key={t.id}
            t={t}
            companyMap={companyMap}
            statusMap={statusLabelMap}
            priorityMap={priorityLabelMap}
            queueMap={queueLabelMap}
          />
        ))}
      </div>
      )}

      {/* Desktop table */}
      {isDesktop && (
      <div className="mt-4 overflow-x-auto rounded-md border">
        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 60 }} />
            {visibleCols.map((c) => (
              <col
                key={c.key}
                ref={(el) => { if (el) colRefs.current.set(c.key, el); else colRefs.current.delete(c.key); }}
                style={{ width: widths[c.key] ?? c.defaultWidth }}
              />
            ))}
          </colgroup>
          <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-2 py-2 text-left">
                <div className="flex items-center gap-0.5">
                  <SelectAllCheckbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    disabled={sortedItems.length === 0}
                    onChange={toggleSelectAll}
                  />
                  <BulkActionsMenu
                    count={selected.size}
                    onBulkEdit={() => setBulkOpen(true)}
                    onClear={() => setSelected(new Set())}
                  />
                </div>
              </th>
              {visibleCols.map((c) => (
                <HeaderCell
                  key={c.key}
                  col={c}
                  sort={sort}
                  onSort={cycleSort}
                  onResizeStart={startResize}
                />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y" {...descTooltip.bind}>
            {sortedItems.length === 0 && !tickets.isLoading ? (
              <tr>
                <td colSpan={visibleCols.length + 1} className="px-3 py-12 text-center text-muted-foreground">
                  No tickets match these filters.
                </td>
              </tr>
            ) : null}
            {sortedItems.map((t) => (
              <TicketRow
                key={t.id}
                t={t}
                selected={selected.has(t.id)}
                cols={visibleCols}
                ctx={cellCtx}
                description={descriptions.get(t.id)}
                onToggle={toggleRow}
              />
            ))}
          </tbody>
        </table>
        {descTooltip.tooltip}
      </div>
      )}

      <BulkEditTicketsDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        ids={Array.from(selected)}
        picklists={{
          status: statusOpts,
          priority: priorityOpts,
          issueType: issueTypeOpts,
          subIssueType: subIssueTypeOpts,
        }}
        onApplied={() => setSelected(new Set())}
      />

      {/* Floating Engineering AI over the tickets this page shows (same as Opportunities / Projects). */}
      <TicketQueueAssistant scope={config.unassignedOnly || filters.unassigned ? 'unassigned' : filters.assignedTo === 'me' ? 'me' : 'all'} />
    </div>
  );
}

// Dropdown of bulk actions, triggered by a small chevron beside the select-all box.
// Kept as a menu (rather than an inline bar) so the grid never shifts, and so more
// actions can be slotted in later.
function BulkActionsMenu({
  count, onBulkEdit, onClear,
}: {
  count: number;
  onBulkEdit: () => void;
  onClear: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-label="Bulk actions"
          title="Bulk actions"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        <DropdownMenuLabel>
          {count > 0 ? `${count} selected` : 'No tickets selected'}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={count === 0} onSelect={onBulkEdit}>
          Bulk edit fields…
        </DropdownMenuItem>
        <DropdownMenuItem disabled={count === 0} onSelect={onClear}>
          Clear selection
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SelectAllCheckbox({
  checked, indeterminate, disabled, onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled?: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <input
      type="checkbox"
      ref={(el) => { if (el) el.indeterminate = indeterminate; }}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 cursor-pointer accent-primary disabled:cursor-default disabled:opacity-40"
      aria-label="Select all tickets"
    />
  );
}

// TicketCard is imported from @/components/cards/TicketCard

// ----- Rows -----

/**
 * One grid row. Memoised: with stable props (shared `ctx`, `cols`, `onToggle`) a keystroke, a checkbox
 * on another row or a column resize doesn't re-render all 200 rows.
 */
const TicketRow = memo(function TicketRow({
  t, selected, cols, ctx, description, onToggle,
}: {
  t: Ticket;
  selected: boolean;
  cols: ColumnDef[];
  ctx: CellCtx;
  description: string | null | undefined;
  onToggle: (id: number, on: boolean) => void;
}) {
  return (
    <tr className={cn('hover:bg-muted/30', selected && 'bg-primary/5')}>
      <td className="px-3 py-2 align-top">
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onToggle(t.id, e.target.checked)}
          className="h-4 w-4 cursor-pointer accent-primary"
          aria-label={`Select ticket ${t.ticketNumber || t.id}`}
        />
      </td>
      {cols.map((c) => (
        <td key={c.key} className={cn('px-3 py-2 align-top overflow-hidden', cellDateClass(c.key, t))}>
          {renderCell(c.key, t, ctx, description)}
        </td>
      ))}
    </tr>
  );
});

// ----- Cell rendering -----

interface CellCtx {
  companyMap: Map<number, string>;
  statusOpts: PicklistValue[];
  priorityOpts: PicklistValue[];
  queueOpts: PicklistValue[];
  issueTypeOpts: PicklistValue[];
  subIssueTypeOpts: PicklistValue[];
  ticketTypeMap: Map<number, string>;
  issueTypeMap: Map<number, string>;
  subIssueTypeMap: Map<number, string>;
  /** Hover-intent prefetch handlers for the ticket links (stable). */
  hoverPrefetch: ReturnType<typeof useTicketHoverPrefetch>;
}

function renderCell(key: ColumnKey, t: Ticket, ctx: CellCtx, description: string | null | undefined): ReactNode {
  switch (key) {
    case 'id': {
      const label = t.ticketNumber || String(t.id);
      return (
        <Link to={ticketPath(t.id)} className="block truncate font-mono text-xs text-brand-strong hover:underline" title={label}
          data-ticket-id={t.id} {...ctx.hoverPrefetch}>
          {label}
        </Link>
      );
    }
    case 'title':
      return (
        <Link to={ticketPath(t.id)} className="block truncate font-medium hover:underline" title={t.title ?? ''}
          data-ticket-id={t.id} {...ctx.hoverPrefetch}>
          {t.title}
        </Link>
      );
    case 'description':
      return <TicketDescriptionCell ticketId={t.id} description={description} />;
    case 'company': {
      const name = t.companyID != null ? ctx.companyMap.get(t.companyID) ?? `#${t.companyID}` : '—';
      return <div className="truncate text-muted-foreground" title={name}>{name}</div>;
    }
    case 'status':
      return <InlineEdit ticketId={t.id} field="status" value={t.status} options={ctx.statusOpts} />;
    case 'priority':
      return <InlineEdit ticketId={t.id} field="priority" value={t.priority} options={ctx.priorityOpts} />;
    case 'queueID':
      return <InlineEdit ticketId={t.id} field="queueID" value={t.queueID} options={ctx.queueOpts} />;
    case 'ticketType': {
      const label = t.ticketType != null ? ctx.ticketTypeMap.get(t.ticketType) ?? `#${t.ticketType}` : '—';
      return <div className="truncate" title={label}>{label}</div>;
    }
    case 'issueType':
      // Changing issueType clears subIssueType in the same PATCH, since the old child
      // value won't usually be valid under the new parent.
      return (
        <InlineEdit
          ticketId={t.id}
          field="issueType"
          value={t.issueType}
          options={ctx.issueTypeOpts}
          extraFields={t.subIssueType != null ? { subIssueType: null } : undefined}
        />
      );
    case 'subIssueType': {
      if (t.issueType == null) {
        return <span className="text-xs italic text-muted-foreground">Set Type first</span>;
      }
      const filtered = ctx.subIssueTypeOpts.filter((o) => Number(o.parentValue) === t.issueType);
      return (
        <InlineEdit
          ticketId={t.id}
          field="subIssueType"
          value={t.subIssueType}
          options={filtered}
        />
      );
    }
    case 'assignee':
      return <AssignedResourcePicker ticketId={t.id} value={t.assignedResourceID ?? null} />;
    case 'dueDateTime':
      return <div className="truncate text-xs">{formatMaybeDate(t.dueDateTime)}</div>;
    case 'lastActivityDate':
      return <div className="truncate text-xs text-muted-foreground">{formatMaybeRelative(t.lastActivityDate)}</div>;
    case 'openInAutotask':
      return (
        <a
          href={`${AUTOTASK_TICKET_URL}${t.id}`}
          target="_blank"
          rel="noreferrer"
          title="Open in Autotask"
          className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      );
  }
}

function cellDateClass(key: ColumnKey, t: Ticket): string {
  if (key !== 'dueDateTime') return '';
  return dueClass(t.dueDateTime);
}

// ----- Sub-components -----

function HeaderCell({
  col, sort, onSort, onResizeStart,
}: {
  col: ColumnDef;
  sort: SortState;
  onSort: (k: SortKey) => void;
  onResizeStart: (k: ColumnKey, e: React.PointerEvent) => void;
}) {
  const active = sort?.key === col.key;
  const Icon = !active ? ArrowUpDown : sort!.dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th className="relative px-3 py-2 text-left font-medium select-none">
      {col.unsortable ? (
        <span className="truncate">{col.label}</span>
      ) : (
        <button
          type="button"
          onClick={() => onSort(col.key as SortKey)}
          className={cn(
            'inline-flex max-w-full items-center gap-1 truncate transition-colors',
            active ? 'text-foreground' : 'hover:text-foreground',
          )}
        >
          <span className="truncate">{col.label}</span>
          <Icon className={cn('h-3 w-3 shrink-0', active ? 'opacity-80' : 'opacity-40')} />
        </button>
      )}
      <div
        onPointerDown={(e) => onResizeStart(col.key, e)}
        className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize touch-none hover:bg-primary/40 active:bg-primary/60"
        title="Drag to resize"
      />
    </th>
  );
}

function ColumnPicker({
  visible, onToggle, onReset, onResetWidths,
}: {
  visible: Set<ColumnKey>;
  onToggle: (k: ColumnKey, on: boolean) => void;
  onReset: () => void;
  onResetWidths: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Desktop grid only: phones get cards, which have no columns. */}
        <Button variant="outline" size="sm" className="hidden h-7 gap-1.5 text-xs md:inline-flex">
          <Columns3 className="h-3.5 w-3.5" /> Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Show columns</DropdownMenuLabel>
        {ALL_COLUMNS.filter((c) => c.label !== '').map((c) => (
          <DropdownMenuCheckboxItem
            key={c.key}
            checked={c.required || visible.has(c.key)}
            disabled={c.required}
            onCheckedChange={(v) => onToggle(c.key, !!v)}
            onSelect={(e) => e.preventDefault()}
          >
            {c.label}{c.required && <span className="ml-2 text-xs text-muted-foreground">(required)</span>}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onReset}>Reset visibility</DropdownMenuItem>
        <DropdownMenuItem onSelect={onResetWidths}>Reset column widths</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function QueueTabs({
  tabs, filters, onChange,
}: {
  tabs: { label: string; id: number | null }[];
  filters: TicketFilters;
  onChange: (next: TicketFilters) => void;
}) {
  const activeId =
    filters.queueID.length === 0
      ? null
      : filters.queueID.length === 1
      ? filters.queueID[0]
      : 'multi';

  return (
    // Scrolls sideways on a phone rather than wrapping one tab onto a line of its own.
    <div className="flex items-center gap-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none]">
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;
        return (
          <button
            key={tab.label}
            type="button"
            onClick={() => onChange({ ...filters, queueID: tab.id === null ? [] : [tab.id] })}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap',
              isActive
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// ----- Helpers -----

function pickToMap(opts: PicklistValue[] | undefined): Map<number, string> {
  const m = new Map<number, string>();
  for (const o of opts ?? []) m.set(Number(o.value), o.label);
  return m;
}

function formatMaybeDate(d: string | null) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

function formatMaybeRelative(d: string | null) {
  if (!d) return '—';
  try {
    return formatDistanceToNow(new Date(d), { addSuffix: true });
  } catch {
    return '—';
  }
}

function dueClass(d: string | null) {
  if (!d) return 'text-muted-foreground';
  const due = new Date(d).getTime();
  const now = Date.now();
  if (due < now) return 'text-destructive font-medium';
  if (due - now < 48 * 60 * 60 * 1000) return 'text-orange-600 dark:text-orange-400';
  return '';
}

function subtitleFor(f: TicketFilters, config: TicketsViewConfig): string {
  const parts: string[] = [];
  if (config.unassignedOnly || f.unassigned) parts.push('Unassigned');
  if (f.assignedTo === 'me') parts.push('Assigned to me');
  if (f.excludeStatus?.length) parts.push('Excluding Complete');
  return parts.join(' · ');
}

// Shared queue-tab presets used by both views.
const STATUS_WORKFLOW_ORDER: Record<string, number> = {
  'new': 0,
  'assigned': 1,
  'in progress': 2,
  'escalated': 3,
  'scheduled': 4,
  'on-hold': 5,
  'waiting customer': 6,
  'customer note added': 7,
  'waiting vendor': 8,
  'waiting parts': 9,
  'completed-pending customer': 10,
  'completed': 11,
};

export const STANDARD_QUEUE_TABS: { label: string; id: number | null }[] = [
  { label: 'All',        id: null     },
  { label: 'Support',    id: 29682833 },
  { label: 'Monitoring', id: 8        },
  { label: 'Projects',   id: 29683489 },
  { label: 'Presales',   id: 29683488 },
];
